import React, { Fragment } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';

/**
 * P&ID / DCS 风格设备形状库
 * 每类设备按工程制图惯例绘制，比例接近真实设备：
 *  - 加热炉 furnace    : 方箱 + 烟囱 + 对角线（辐射段）
 *  - 反应器 reactor    : 竖长容器（高:宽 ≈ 3:1）带催化剂床层虚线 + 裙座
 *  - 分馏塔 column     : 细长容器（高:宽 ≈ 3.6:1）带塔盘短线
 *  - 卧式罐 hDrum      : 椭圆封头卧式容器 + 支腿
 *  - 立式罐 vDrum      : 立式分离器，带液位线 + 裙座
 *  - 换热器 exchanger  : 圆形（内部折流虚线）
 *  - 泵 pump           : 圆 + 内接三角（离心泵符号）
 *  - 压缩机 compressor : 梯形
 *  - 再生器 regenerator: 立式分段容器（烧焦 / 氯化更新 / 干燥）
 *  - 混合点 mixer      : 小圆点
 */

export type EquipKind =
  | 'furnace'
  | 'reactor'
  | 'column'
  | 'hDrum'
  | 'vDrum'
  | 'exchanger'
  | 'pump'
  | 'compressor'
  | 'regenerator'
  | 'mixer'
  // 造纸（制浆）设备
  | 'digester'
  | 'blowTank'
  | 'tower'
  | 'washer'
  | 'clarifier'
  | 'limeKiln'
  | 'refiner';

export interface EquipData extends Record<string, unknown> {
  kind: EquipKind;
  tag: string;
  name: string;
  note?: string;
  dark: boolean;
}

type Palette = { bg: string; stroke: string };

const EQUIP_PALETTE: Record<EquipKind, { light: Palette; dark: Palette }> = {
  furnace: { light: { bg: '#ffe082', stroke: '#e65100' }, dark: { bg: '#5d4a12', stroke: '#ffca28' } },
  reactor: { light: { bg: '#e8eaf6', stroke: '#3949ab' }, dark: { bg: '#1a2440', stroke: '#7986cb' } },
  column: { light: { bg: '#e0f2f1', stroke: '#00796b' }, dark: { bg: '#0e2b28', stroke: '#4db6ac' } },
  hDrum: { light: { bg: '#eceff1', stroke: '#455a64' }, dark: { bg: '#263238', stroke: '#90a4ae' } },
  vDrum: { light: { bg: '#eceff1', stroke: '#455a64' }, dark: { bg: '#263238', stroke: '#90a4ae' } },
  exchanger: { light: { bg: '#e3f2fd', stroke: '#1565c0' }, dark: { bg: '#0d2744', stroke: '#64b5f6' } },
  pump: { light: { bg: '#fce4ec', stroke: '#ad1457' }, dark: { bg: '#3a1225', stroke: '#f06292' } },
  compressor: { light: { bg: '#f3e5f5', stroke: '#6a1b9a' }, dark: { bg: '#2a1240', stroke: '#ba68c8' } },
  regenerator: { light: { bg: '#ede7f6', stroke: '#4527a0' }, dark: { bg: '#231a3d', stroke: '#9575cd' } },
  mixer: { light: { bg: '#37474f', stroke: '#37474f' }, dark: { bg: '#cfd8dc', stroke: '#cfd8dc' } },
  digester: { light: { bg: '#e8eaf6', stroke: '#3949ab' }, dark: { bg: '#1a2440', stroke: '#7986cb' } },
  blowTank: { light: { bg: '#eceff1', stroke: '#455a64' }, dark: { bg: '#263238', stroke: '#90a4ae' } },
  tower: { light: { bg: '#e0f2f1', stroke: '#00796b' }, dark: { bg: '#0e2b28', stroke: '#4db6ac' } },
  washer: { light: { bg: '#e3f2fd', stroke: '#1565c0' }, dark: { bg: '#0d2744', stroke: '#64b5f6' } },
  clarifier: { light: { bg: '#eceff1', stroke: '#455a64' }, dark: { bg: '#263238', stroke: '#90a4ae' } },
  limeKiln: { light: { bg: '#ffe082', stroke: '#e65100' }, dark: { bg: '#5d4a12', stroke: '#ffca28' } },
  refiner: { light: { bg: '#ffe0b2', stroke: '#bf360c' }, dark: { bg: '#4a2410', stroke: '#ffab91' } },
};

export const EQUIP_SIZES: Record<EquipKind, { w: number; h: number }> = {
  furnace: { w: 100, h: 128 },
  reactor: { w: 52, h: 164 },
  column: { w: 64, h: 232 },
  hDrum: { w: 132, h: 56 },
  vDrum: { w: 58, h: 112 },
  exchanger: { w: 58, h: 58 },
  pump: { w: 44, h: 48 },
  compressor: { w: 66, h: 48 },
  regenerator: { w: 100, h: 260 },
  mixer: { w: 16, h: 16 },
  digester: { w: 64, h: 190 },
  blowTank: { w: 84, h: 150 },
  tower: { w: 56, h: 170 },
  washer: { w: 58, h: 58 },
  clarifier: { w: 90, h: 70 },
  limeKiln: { w: 150, h: 56 },
  refiner: { w: 76, h: 62 },
};

const FurnaceShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="38" y="0" width="24" height="22" fill={bg} stroke={stroke} strokeWidth="2" />
    <rect x="0" y="22" width="100" height="94" rx="4" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="2" y1="24" x2="98" y2="114" stroke={stroke} strokeWidth="1.4" />
    <line x1="98" y1="24" x2="2" y2="114" stroke={stroke} strokeWidth="1.4" />
    <rect x="34" y="116" width="32" height="12" fill={bg} stroke={stroke} strokeWidth="2" />
  </g>
);

const ReactorShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="8" y="0" width="36" height="146" rx="17" fill={bg} stroke={stroke} strokeWidth="2" />
    {[42, 76, 110].map((y) => (
      <line key={y} x1="12" y1={y} x2="40" y2={y} stroke={stroke} strokeWidth="1.4" strokeDasharray="4 3" />
    ))}
    <line x1="17" y1="146" x2="13" y2="164" stroke={stroke} strokeWidth="2" />
    <line x1="35" y1="146" x2="39" y2="164" stroke={stroke} strokeWidth="2" />
  </g>
);

const ColumnShape: React.FC<Palette> = ({ bg, stroke }) => {
  const trays = [];
  for (let i = 0; i < 9; i++) {
    const y = 34 + i * 20;
    const long = i % 2 === 0;
    trays.push(
      <line key={y} x1={long ? 6 : 16} y1={y} x2={long ? 58 : 48} y2={y} stroke={stroke} strokeWidth="1.3" />
    );
  }
  return (
    <g>
      <rect x="22" y="0" width="20" height="8" rx="3" fill={bg} stroke={stroke} strokeWidth="1.6" />
      <rect x="2" y="6" width="60" height="220" rx="24" fill={bg} stroke={stroke} strokeWidth="2" />
      {trays}
      <line x1="20" y1="226" x2="16" y2="232" stroke={stroke} strokeWidth="2" />
      <line x1="44" y1="226" x2="48" y2="232" stroke={stroke} strokeWidth="2" />
    </g>
  );
};

const HDrumShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="12" y="4" width="108" height="40" rx="20" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="34" y1="24" x2="98" y2="24" stroke={stroke} strokeWidth="1.2" strokeDasharray="5 3" />
    <line x1="34" y1="44" x2="28" y2="56" stroke={stroke} strokeWidth="2" />
    <line x1="98" y1="44" x2="104" y2="56" stroke={stroke} strokeWidth="2" />
  </g>
);

const VDrumShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="9" y="0" width="40" height="96" rx="19" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="13" y1="58" x2="45" y2="58" stroke={stroke} strokeWidth="1.2" strokeDasharray="5 3" />
    <line x1="18" y1="96" x2="14" y2="112" stroke={stroke} strokeWidth="2" />
    <line x1="40" y1="96" x2="44" y2="112" stroke={stroke} strokeWidth="2" />
  </g>
);

const ExchangerShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="25" y="0" width="8" height="6" fill={bg} stroke={stroke} strokeWidth="1.4" />
    <rect x="25" y="46" width="8" height="6" fill={bg} stroke={stroke} strokeWidth="1.4" />
    <circle cx="29" cy="26" r="23" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="29" y1="9" x2="29" y2="43" stroke={stroke} strokeWidth="1.4" strokeDasharray="3 3" />
  </g>
);

const PumpShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <circle cx="22" cy="20" r="15" fill={bg} stroke={stroke} strokeWidth="2" />
    <path d="M12,12 L33,20 L12,28 Z" fill="none" stroke={stroke} strokeWidth="1.6" />
    <line x1="22" y1="35" x2="22" y2="43" stroke={stroke} strokeWidth="2" />
    <line x1="8" y1="43" x2="36" y2="43" stroke={stroke} strokeWidth="2" />
  </g>
);

const CompressorShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <path d="M8,38 L20,6 L46,6 L58,38 Z" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="8" y1="44" x2="58" y2="44" stroke={stroke} strokeWidth="2" />
  </g>
);

const RegeneratorShape: React.FC<Palette & { text: string }> = ({ bg, stroke, text }) => (
  <g>
    <rect x="4" y="0" width="92" height="252" rx="16" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="6" y1="84" x2="94" y2="84" stroke={stroke} strokeWidth="1.6" />
    <line x1="6" y1="158" x2="94" y2="158" stroke={stroke} strokeWidth="1.6" />
    {[
      { y: 44, label: '烧焦 ~500℃' },
      { y: 121, label: '氯化更新' },
      { y: 205, label: '干燥 / 冷却' },
    ].map((s) => (
      <Fragment key={s.y}>
        <line x1="14" y1={s.y + 22} x2="18" y2={s.y + 22} stroke={stroke} strokeWidth="1" strokeDasharray="2 2" />
        <text x="50" y={s.y} textAnchor="middle" fontSize="9" fontWeight="500" fill={text}>{s.label}</text>
      </Fragment>
    ))}
  </g>
);

const MixerShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <circle cx="8" cy="8" r="6" fill={bg} stroke={stroke} strokeWidth="1.6" />
  </g>
);

/** 蒸煮器：顶部木片口 + 直筒（蒸煮区虚线）+ 锥底（连续蒸煮塔） */
const DigesterShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="24" y="0" width="16" height="10" fill={bg} stroke={stroke} strokeWidth="1.6" />
    <rect x="6" y="10" width="52" height="118" rx="6" fill={bg} stroke={stroke} strokeWidth="2" />
    {[58, 92].map((y) => (
      <line key={y} x1="11" y1={y} x2="53" y2={y} stroke={stroke} strokeWidth="1.3" strokeDasharray="4 3" />
    ))}
    <path d="M10,128 L54,128 L36,182 L28,182 Z" fill={bg} stroke={stroke} strokeWidth="2" />
  </g>
);

/** 喷放锅：矮胖锥底立罐（间歇放料缓冲） */
const BlowTankShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="34" y="0" width="16" height="10" fill={bg} stroke={stroke} strokeWidth="1.6" />
    <rect x="8" y="10" width="68" height="90" rx="10" fill={bg} stroke={stroke} strokeWidth="2" />
    <path d="M10,100 L74,100 L50,142 L34,142 Z" fill={bg} stroke={stroke} strokeWidth="2" />
  </g>
);

/** 漂白/反应塔：无内件升流塔（顶进底出锥） */
const TowerShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="20" y="0" width="16" height="10" fill={bg} stroke={stroke} strokeWidth="1.6" />
    <rect x="4" y="8" width="48" height="142" rx="18" fill={bg} stroke={stroke} strokeWidth="2" />
    <path d="M8,150 L48,150 L34,166 L22,166 Z" fill={bg} stroke={stroke} strokeWidth="2" />
  </g>
);

/** 鼓式洗浆机：转鼓半浸浆槽（同心圆 = 鼓面） */
const WasherShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="4" y="34" width="50" height="20" rx="8" fill={bg} stroke={stroke} strokeWidth="2" />
    <circle cx="29" cy="26" r="19" fill={bg} stroke={stroke} strokeWidth="2" />
    <circle cx="29" cy="26" r="10" fill="none" stroke={stroke} strokeWidth="1.3" />
    <circle cx="29" cy="26" r="2.4" fill={stroke} />
  </g>
);

/** 澄清器：宽扁立罐（中心耙臂 + 液位线） */
const ClarifierShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="38" y="0" width="14" height="8" fill={bg} stroke={stroke} strokeWidth="1.6" />
    <rect x="4" y="8" width="82" height="56" rx="10" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="8" y1="26" x2="82" y2="26" stroke={stroke} strokeWidth="1.2" strokeDasharray="5 3" />
    <line x1="45" y1="26" x2="45" y2="58" stroke={stroke} strokeWidth="1.4" />
    <line x1="45" y1="54" x2="22" y2="61" stroke={stroke} strokeWidth="1.4" />
    <line x1="45" y1="54" x2="68" y2="61" stroke={stroke} strokeWidth="1.4" />
  </g>
);

/** 石灰窑：横长回转圆筒 + 窑头火嘴箱 + 两组支撑轮 */
const LimeKilnShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <rect x="0" y="14" width="16" height="24" rx="3" fill={bg} stroke={stroke} strokeWidth="2" />
    <rect x="16" y="10" width="134" height="34" rx="17" fill={bg} stroke={stroke} strokeWidth="2" />
    <line x1="26" y1="27" x2="140" y2="27" stroke={stroke} strokeWidth="1.2" strokeDasharray="5 4" />
    <circle cx="62" cy="50" r="5" fill={bg} stroke={stroke} strokeWidth="1.6" />
    <circle cx="104" cy="50" r="5" fill={bg} stroke={stroke} strokeWidth="1.6" />
  </g>
);

/** 磨浆机/磨木机：双圆盘（磨盘）+ 主轴 + 电机底座（机械制浆专用） */
const RefinerShape: React.FC<Palette> = ({ bg, stroke }) => (
  <g>
    <circle cx="24" cy="26" r="22" fill={bg} stroke={stroke} strokeWidth="2" />
    <circle cx="52" cy="26" r="22" fill={bg} stroke={stroke} strokeWidth="2" />
    <circle cx="24" cy="26" r="9" fill="none" stroke={stroke} strokeWidth="1.4" />
    <circle cx="52" cy="26" r="9" fill="none" stroke={stroke} strokeWidth="1.4" />
    <line x1="24" y1="48" x2="24" y2="58" stroke={stroke} strokeWidth="2" />
    <line x1="52" y1="48" x2="52" y2="58" stroke={stroke} strokeWidth="2" />
    <line x1="12" y1="58" x2="64" y2="58" stroke={stroke} strokeWidth="2" />
  </g>
);

const SHAPES: Record<EquipKind, React.FC<Palette & { text: string }>> = {
  furnace: FurnaceShape as never,
  reactor: ReactorShape as never,
  column: ColumnShape as never,
  hDrum: HDrumShape as never,
  vDrum: VDrumShape as never,
  exchanger: ExchangerShape as never,
  pump: PumpShape as never,
  compressor: CompressorShape as never,
  regenerator: RegeneratorShape,
  mixer: MixerShape as never,
  digester: DigesterShape as never,
  blowTank: BlowTankShape as never,
  tower: TowerShape as never,
  washer: WasherShape as never,
  clarifier: ClarifierShape as never,
  limeKiln: LimeKilnShape as never,
  refiner: RefinerShape as never,
};

const SIDES = [Position.Top, Position.Bottom, Position.Left, Position.Right] as const;

/** 四侧透明 handle 对（source + target 重叠），供所有图上节点复用；id 形如 top-s / top-t */
export const SideHandles: React.FC = () => (
  <>
    {SIDES.map((side) => (
      <Fragment key={side}>
        <Handle type="source" position={side} id={`${side}-s`} style={handleStyleOf(side)} isConnectable={false} />
        <Handle type="target" position={side} id={`${side}-t`} style={handleStyleOf(side)} isConnectable={false} />
      </Fragment>
    ))}
  </>
);

function handleStyleOf(side: (typeof SIDES)[number]): React.CSSProperties {
  const base: React.CSSProperties = {
    opacity: 0,
    width: 8,
    height: 8,
    border: 'none',
    background: 'transparent',
  };
  if (side === Position.Top) return { ...base, top: -4, left: '50%' };
  // 底部管口放在左下角外：竖向管线避开正下方的三行设备标签（标签避让规则）
  if (side === Position.Bottom) return { ...base, bottom: -4, left: -7 };
  if (side === Position.Left) return { ...base, left: -4, top: '50%' };
  return { ...base, right: -4, top: '50%' };
}

export const EquipmentNode: React.FC<NodeProps> = ({ data }) => {
  const d = data as EquipData;
  const { w, h } = EQUIP_SIZES[d.kind];
  const palette = EQUIP_PALETTE[d.kind][d.dark ? 'dark' : 'light'];
  const Shape = SHAPES[d.kind];
  const textMain = d.dark ? '#ffffff' : '#263238';
  const textSub = d.dark ? '#b0bec5' : '#546e7a';
  return (
    <div style={{ width: w, position: 'relative' }}>
      <svg width={w} height={h} style={{ display: 'block' }}>
        <Shape {...palette} text={d.dark ? '#e0e0e0' : '#37474f'} />
      </svg>
      {d.kind !== 'mixer' && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
          <div className="pid-label" style={{ width: 'fit-content', textAlign: 'center', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: textSub }}>{d.name}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: textMain }}>{d.tag}</div>
            {d.note && <div style={{ fontSize: 10, color: d.dark ? '#ffb74d' : '#e65100' }}>{d.note}</div>}
          </div>
        </div>
      )}
      <SideHandles />
    </div>
  );
};
